// Single source of truth for every color used anywhere in the chart engine
// (chrome AND SVG canvas). Anchored to the app's real homepage identity --
// violet/indigo/blue on near-black -- not the old generic electric-cyan
// palette (#00d4ff) charts used before this rebuild. Replaces chartStyles.js,
// findicators/colors.js, and MACD.js's local fallback palette (all deleted).
//
// 2026-09-11: this was the ONLY theme -- the whole chart engine rendered
// dark regardless of the site's light/dark toggle. darkTheme below is
// untouched (still the default export, so the ~10 indicator files that
// import `theme` directly for categorical colors -- SMA/EMA/MACD lines,
// bull/bear fills -- keep working unchanged; those hues read fine on
// either background and don't need a light variant). lightTheme only
// overrides the *surface* tokens (background/grid/axis/border/crosshair/
// tooltip) -- the ones that actually need to flip with the page theme.
// ChartEngine.jsx/chart.jsx/OhlcLegendHeader.jsx pick between the two via
// the app's own useTheme(); every other chart module keeps receiving
// `theme` as a plain parameter exactly as before, so this is additive.

const darkTheme = {
  background: {
    app: '#09090f',
    panel: '#0d0d16',
    chart: '#09090f',
  },

  grid: {
    color: '#1c1c2a',
    opacity: 0.5,
    dasharray: '2,3',
  },

  axis: {
    text: '#8b8ca3',
    line: '#232333',
    fontFamily: '"Inter", system-ui, sans-serif',
    fontSize: '11px',
  },

  border: 'rgba(167,139,250,0.14)',

  brand: {
    violet: '#a78bfa',
    indigo: '#818cf8',
    blue: '#60a5fa',
  },

  // Primary mark color for Line/Area/Bar/Baseline's line component --
  // brand-forward, deliberately distinct from bull/bear semantic colors.
  primaryLine: '#818cf8',
  primaryGlow: 'rgba(129,140,248,0.35)',
  areaGradientTop: 'rgba(129,140,248,0.28)',
  areaGradientBottom: 'rgba(129,140,248,0)',

  // Semantic bull/bear -- emerald/rose family, matching the rest of the app
  // (StockHeader.jsx, retention-toast work, etc.), not generic Tailwind
  // green-500/red-500.
  bull: { strong: '#10b981', soft: '#34d399' },
  bear: { strong: '#f43f5e', soft: '#fb7185' },

  crosshair: {
    line: 'rgba(226,226,240,0.35)',
    dasharray: '2,3',
    tagBackground: '#818cf8',
    tagText: '#09090f',
  },

  tooltip: {
    background: 'rgba(13,13,22,0.92)',
    border: 'rgba(167,139,250,0.18)',
    text: '#e5e5f0',
    muted: '#8b8ca3',
  },

  volume: {
    bull: 'rgba(16,185,129,0.55)',
    bear: 'rgba(244,63,94,0.55)',
  },

  indicators: {
    sma: '#fbbf24',
    ema: '#38bdf8',
    bollinger: { upper: '#a78bfa', middle: '#818cf8', lower: '#a78bfa' },
    keltner: { upper: '#e879f9', middle: '#c084fc', lower: '#e879f9' },
    atr: '#f59e0b',
    parabolicSar: { bullish: '#34d399', bearish: '#fb7185' },
    ichimoku: {
      tenkan: '#60a5fa',
      kijun: '#f97316',
      cloudBull: 'rgba(16,185,129,0.14)',
      cloudBear: 'rgba(244,63,94,0.14)',
      chikou: '#a78bfa',
    },
    macd: { line: '#818cf8', signal: '#f59e0b', histogramPos: '#10b981', histogramNeg: '#f43f5e' },
    rsi: { line: '#c084fc', overbought: '#f43f5e', oversold: '#34d399' },
    stochastic: { k: '#60a5fa', d: '#f59e0b', overbought: '#f43f5e', oversold: '#34d399' },
    adx: '#facc15',
    obv: '#38bdf8',
    cmf: '#22d3ee',
    cci: '#f472b6',
    williamsR: '#fb923c',
    aroon: { up: '#34d399', down: '#f43f5e' },
    mfi: '#c084fc',
    roc: '#38bdf8',
    cmo: '#f59e0b',
    rvi: { line: '#818cf8', signal: '#f59e0b' },
    elderRay: { bull: '#34d399', bear: '#f43f5e' },
  },

  typography: {
    fontFamily: '"Inter", system-ui, sans-serif',
    tabularNums: 'tabular-nums',
  },
};

// Light variant: only the surface tokens change. brand/primaryLine/bull/
// bear/indicators/volume/typography are spread in unchanged -- those hues
// were chosen to be legible on either a near-black or a white canvas, and
// changing them would also mean re-tuning every indicator file's hardcoded
// import of the dark object (see the file-level comment above for why that
// was deliberately avoided).
const lightTheme = {
  ...darkTheme,
  background: {
    app: '#ffffff',
    panel: '#f7f7fb',
    chart: '#ffffff',
  },
  grid: {
    color: '#e4e4ee',
    opacity: 0.8,
    dasharray: '2,3',
  },
  axis: {
    ...darkTheme.axis,
    text: '#57576f',
    line: '#d5d5e2',
  },
  border: 'rgba(124,58,237,0.16)',
  crosshair: {
    line: 'rgba(30,30,45,0.35)',
    dasharray: '2,3',
    tagBackground: '#7c3aed',
    tagText: '#ffffff',
  },
  tooltip: {
    background: 'rgba(255,255,255,0.97)',
    border: 'rgba(124,58,237,0.2)',
    text: '#1c1c2a',
    muted: '#65657a',
  },
};

export { darkTheme, lightTheme };
export default darkTheme;
